import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLocalStorage } from 'common/hooks';
import { useDeviceBrowser } from 'common/hooks/useDeviceBrowser';
import useUser from 'common/hooks/useUser';

import InstallationDialog from './InstallationDialog';
import { IosInstallGuideModal } from './IosInstallGuideModal';
import { telemetryClickInstallFromBanner, telemetryPWAInstalled, telemetryClickIOSInstructions } from './telemetry';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type PWAContextData = {
  isInstructionsModalOpen: boolean;
  isSupported: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
  shouldShowInstallGeneralPrompt: boolean;
  shouldShowInstallIOSPrompt: boolean;
  install: () => void;
  setIsInstructionsModalOpen: (isOpen: boolean) => void;
};

export const PWAContext = React.createContext<PWAContextData>({
  isInstructionsModalOpen: false,
  isSupported: false,
  shouldShowInstallGeneralPrompt: false,
  shouldShowInstallIOSPrompt: false,
  deferredPrompt: null,
  install: () => null,
  setIsInstructionsModalOpen: () => null,
});

export const PWAContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState<boolean>(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showDialogOn, setShowDialogOn] = useLocalStorage('pwa.dialog.date', DateTime.now().toISO());
  const deviceBrowser = useDeviceBrowser();
  const { isEmployee } = useUser();

  const contextValue = useMemo<PWAContextData>(() => {
    const isSupported = !!deferredPrompt;
    const { isMobile, system } = deviceBrowser;

    return {
      isSupported,
      shouldShowInstallGeneralPrompt: isMobile && isSupported && isEmployee && system.name !== 'iOS',
      shouldShowInstallIOSPrompt: isMobile && isEmployee && system.name === 'iOS',
      deferredPrompt,
      isInstructionsModalOpen,
      setIsInstructionsModalOpen,
      install: async () => {
        if (deferredPrompt) {
          deferredPrompt.prompt();

          const { outcome } = await deferredPrompt.userChoice;

          if (outcome === 'accepted') {
            telemetryPWAInstalled();
          }
        }
      },
    };
  }, [deviceBrowser, isEmployee, deferredPrompt, isInstructionsModalOpen]);

  const shouldShowDialog = useMemo(() => {
    if (contextValue.shouldShowInstallGeneralPrompt || contextValue.shouldShowInstallIOSPrompt) {
      return DateTime.fromISO(showDialogOn) < DateTime.now();
    }

    return false;
  }, [contextValue.shouldShowInstallGeneralPrompt, contextValue.shouldShowInstallIOSPrompt, showDialogOn]);

  const handleDismissDialog = () => {
    setShowDialogOn(DateTime.now().plus({ days: 14 }).toISO());
  };

  const handleDialogButtonClick = () => {
    if (contextValue.shouldShowInstallIOSPrompt) {
      telemetryClickIOSInstructions();
      contextValue.setIsInstructionsModalOpen(true);
    } else {
      telemetryClickInstallFromBanner();
      contextValue.install();
    }
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  return (
    <PWAContext.Provider value={contextValue}>
      {shouldShowDialog && (
        <InstallationDialog
          buttonLabel={
            <FormattedMessage
              id={contextValue.shouldShowInstallIOSPrompt ? 'pwa.instructions.label' : 'pwa.install.action'}
            />
          }
          onButtonClick={handleDialogButtonClick}
          onCrossClick={handleDismissDialog}
        />
      )}

      {children}
      <IosInstallGuideModal isOpen={isInstructionsModalOpen} onClose={() => setIsInstructionsModalOpen(false)} />
    </PWAContext.Provider>
  );
};
